/**
 * Humanizes a typed `CircuitOp` into a one-line readable summary, used
 * inside the IterationCard and the snapshot timeline.
 *
 * Per IR plan §6.10 (op-log compression): "long agent sessions produce
 * 50+ ops". Summarizing client-side is the cheapest path — the typed-op
 * shape already carries everything needed for a faithful description,
 * and avoiding a server roundtrip per iteration keeps chat snappy.
 *
 * Contract: each op kind maps to a verb + target. For ops touching a
 * named handle we surface the handle (`r_snubber`); for those keyed by
 * UUID we shorten the id ("comp-8b7c…"). The summary is intentionally
 * stable across re-renders so the card doesn't visually thrash.
 */
import type { CircuitOp } from "@models/circuit-ir";
import AddIcon from "@mui/icons-material/AddRounded";
import RewriteIcon from "@mui/icons-material/AutoFixHighRounded";
import ConnectIcon from "@mui/icons-material/CallSplitRounded";
import EditIcon from "@mui/icons-material/EditRounded";
import RemoveIcon from "@mui/icons-material/RemoveRounded";
import SwapIcon from "@mui/icons-material/SwapHorizRounded";
import { Box, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

interface OpSummaryLineProps {
  op: CircuitOp;
  /** When true, render only the icon + main verb (no targets / values).
   *  Useful for the dense op-timeline strip. Defaults to false. */
  compact?: boolean;
}

interface Summary {
  verb: string;
  target?: string;
  detail?: string;
  icon: ReactNode;
  /** Tone — drives icon + verb color. */
  tone: "additive" | "destructive" | "edit" | "swap" | "rewrite";
}

function shortRef(ref: string): string {
  // UUID detection — fixed-shape `xxxxxxxx-xxxx-…`. Anything else (a
  // string handle like "r_snubber") is rendered as-is.
  return /^[0-9a-f]{8}-/i.test(ref) ? `${ref.slice(0, 8)}…` : ref;
}

function formatValueChange(field: string, value: unknown): string {
  if (value == null) return `${field} → —`;
  if (typeof value === "boolean")
    return `${field} → ${value ? "true" : "false"}`;
  return `${field} → ${String(value)}`;
}

export function summarizeOp(op: CircuitOp): Summary {
  switch (op.kind) {
    case "add_component":
      return {
        verb: "Added",
        target:
          op.refdes ||
          op.handle ||
          `${op.component_kind}${op.value ? ` (${op.value})` : ""}`,
        detail: op.value
          ? `${op.component_kind}, ${op.value}`
          : op.component_kind,
        icon: <AddIcon fontSize="small" />,
        tone: "additive",
      };
    case "add_net":
      return {
        verb: "Added net",
        target: op.name || op.handle || "(anonymous)",
        detail: op.is_power ? "power" : op.is_ground ? "ground" : undefined,
        icon: <AddIcon fontSize="small" />,
        tone: "additive",
      };
    case "connect":
      return {
        verb: "Connected",
        target: `${shortRef(op.component)}.${op.pin}`,
        detail: `→ ${shortRef(op.net)}`,
        icon: <ConnectIcon fontSize="small" />,
        tone: "edit",
      };
    case "disconnect":
      return {
        verb: "Disconnected",
        target: `${shortRef(op.component)}.${op.pin}`,
        detail: `from ${shortRef(op.net)}`,
        icon: <RemoveIcon fontSize="small" />,
        tone: "destructive",
      };
    case "remove_component":
      return {
        verb: "Removed",
        target: shortRef(op.component),
        icon: <RemoveIcon fontSize="small" />,
        tone: "destructive",
      };
    case "remove_net":
      return {
        verb: "Removed net",
        target: shortRef(op.net),
        icon: <RemoveIcon fontSize="small" />,
        tone: "destructive",
      };
    case "change_value":
      return {
        verb: "Changed",
        target: shortRef(op.component),
        detail: formatValueChange(op.field, op.new_value),
        icon: <EditIcon fontSize="small" />,
        tone: "edit",
      };
    case "set_extra":
      return {
        verb: "Set",
        target: `${shortRef(op.component)}.${op.key}`,
        detail: `= ${op.value}`,
        icon: <EditIcon fontSize="small" />,
        tone: "edit",
      };
    case "rename_net":
      return {
        verb: "Renamed net",
        target: shortRef(op.net),
        detail: op.new_name ? `→ ${op.new_name}` : "→ (anonymous)",
        icon: <EditIcon fontSize="small" />,
        tone: "edit",
      };
    case "substitute_mpn":
      return {
        verb: "Substituted",
        target: shortRef(op.component),
        detail: op.new_manufacturer
          ? `${op.new_mpn} (${op.new_manufacturer})`
          : op.new_mpn,
        icon: <SwapIcon fontSize="small" />,
        tone: "swap",
      };
    case "replace_subcircuit":
      return {
        verb: "Replaced subcircuit",
        target: op.label,
        detail: `${op.remove?.length ?? 0} removed, ${op.add?.length ?? 0} added`,
        icon: <RewriteIcon fontSize="small" />,
        tone: "rewrite",
      };
  }
}

const TONE_COLOR: Record<Summary["tone"], string> = {
  additive: "success.main",
  destructive: "error.main",
  edit: "primary.main",
  swap: "secondary.main",
  rewrite: "secondary.main",
};

export function OpSummaryLine({ op, compact = false }: OpSummaryLineProps) {
  const s = summarizeOp(op);
  const color = TONE_COLOR[s.tone];

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ minWidth: 0, py: compact ? 0.125 : 0.375 }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          bgcolor: (t) =>
            s.tone === "additive"
              ? t.palette.success.main + "14"
              : s.tone === "destructive"
                ? t.palette.error.main + "14"
                : t.palette.accent.softer,
          flexShrink: 0,
        }}
      >
        {s.icon}
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.primary",
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {s.verb}
      </Typography>
      {s.target && (
        <Typography
          variant="body2"
          sx={(t) => ({
            color: "text.primary",
            fontFamily: t.fonts.mono,
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          })}
        >
          {s.target}
        </Typography>
      )}
      {s.detail && !compact && (
        <Typography
          variant="body2"
          sx={(t) => ({
            color: "text.secondary",
            fontFamily: t.fonts.mono,
            fontSize: 11,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          })}
        >
          · {s.detail}
        </Typography>
      )}
    </Stack>
  );
}
